import React, { useState, useEffect, useRef } from 'react';
import {
  Clock,
  User,
  CheckCircle,
  Loader2,
  CreditCard,
  LogOut,
  Radio,
  X,
  AlertCircle,
  BookOpen,
  Users as UsersIcon,
  ArrowRight,
  ArrowLeft,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import SchoolHeader from '../components/SchoolHeader';
import KiosqueGoodbyeScreen from '../components/KiosqueGoodbyeScreen';
import { api } from '../services/api';

/*
 * LE KIOSQUE DE POINTAGE
 *
 * Refait le 31/10/2026 à la demande de Daniel : le panneau bleu affiche les
 * règles de pointage du jour, venues des Paramètres de pointage et calculées
 * par le code même de la borne (GET /api/pointages/kiosque/regles). Le statut
 * « en ligne », la date en double et les trois instructions génériques sont
 * retirés.
 *
 * La lecture obligatoire : avant son premier pointage, et à chaque changement
 * des règles qui la concernent, une personne voit ses règles (et ses cours du
 * jour pour un enseignant). Après un temps de lecture minimal, elle confirme en
 * REPASSANT SON BADGE - le kiosque n'a qu'un lecteur - et son pointage est
 * enregistré à l'heure de son premier passage : lire ne la met pas en retard.
 * Le serveur dit si la lecture est requise ; tant que sa table n'existe pas,
 * personne n'est bloqué.
 *
 * Un badge refusé dit quand revenir (« Revenez badger à partir de 09:15 »).
 */

const DELAI_ABANDON_LECTURE_MS = 2 * 60 * 1000;
const RAFRAICHIR_REGLES_MS = 10 * 60 * 1000;

const formatDateForAPI = (date) => date.getFullYear() + '-'
  + String(date.getMonth() + 1).padStart(2, '0') + '-'
  + String(date.getDate()).padStart(2, '0');

// Une couleur par population, celle de l'écran de résultat : sarcelle pour le
// personnel à horaire fixe, violet pour les enseignants payés à l'heure. Les
// cartes du panneau et l'écran de lecture la reprennent (Daniel, 31/10/2026).
const TEINTES = {
  personnel: { fonce: '#319190', clair: '#e3f2f1', texte: '#22706f' },
  enseignant: { fonce: '#6f4bb8', clair: '#efe9fa', texte: '#5b3a9e' }
};

/** Les règles d'une personne, sur deux colonnes : un intitulé court, une phrase. */
const Consignes = ({ consignes, couleur }) => (
  <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
    {consignes.map((c) => (
      <div key={c.titre}>
        <dt className="text-xs font-bold uppercase tracking-wide" style={{ color: couleur }}>{c.titre}</dt>
        <dd className="text-[15px] leading-snug text-gray-800">{c.texte}</dd>
      </div>
    ))}
  </dl>
);

/** Une carte du panneau bleu : un bandeau de couleur, une étiquette par règle. */
const CarteRegles = ({ titre, teinte, etiquette, children }) => (
  <div className="rounded-2xl bg-white shadow-lg overflow-hidden animate-fade-in">
    <div className="flex items-center justify-between gap-3 px-5 py-2.5" style={{ backgroundColor: teinte.fonce }}>
      <span className="text-[16px] font-bold text-white">{titre}</span>
      {etiquette && (
        <span className="rounded-lg bg-white/20 px-2.5 py-0.5 text-[13px] font-bold text-white whitespace-nowrap">{etiquette}</span>
      )}
    </div>
    <div className="px-5 py-3.5">{children}</div>
  </div>
);

const LignesRegles = ({ consignes, teinte }) => (
  <div className="space-y-2.5">
    {consignes.map((c) => (
      <div key={c.titre} className="grid grid-cols-[92px_1fr] items-start gap-3">
        <span
          className="rounded-lg px-2 py-1 text-center text-[11px] font-bold uppercase leading-tight tracking-wide"
          style={{ backgroundColor: teinte.clair, color: teinte.texte }}
        >
          {c.titre}
        </span>
        <span className="text-[14px] leading-snug text-gray-800">{c.texte}</span>
      </div>
    ))}
  </div>
);

const KiosquePointage = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showGoodbye, setShowGoodbye] = useState(false);

  // attente : l'horloge ; lecture : les règles d'une personne à lire ;
  // pointage : le résultat d'un badge.
  const [mode, setMode] = useState('attente');
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [employeeIdentified, setEmployeeIdentified] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Statut du pointage
  const [pointageStatus, setPointageStatus] = useState(null);
  const [pointageMessage, setPointageMessage] = useState('');
  const [revenirA, setRevenirA] = useState(null);

  // Enseignants HORAIRE / personnel FIXE
  const [creneauInfo, setCreneauInfo] = useState(null);
  const [groupeInfo, setGroupeInfo] = useState(null);

  // Les règles du jour (panneau bleu) et la lecture en cours
  const [reglesDuJour, setReglesDuJour] = useState(null);
  const [lecture, setLecture] = useState(null);

  const [user] = useState(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      return JSON.parse(storedUser);
    }
    return { prenom: '', nom: '', role_principal: { nom: '' } };
  });

  // Le lecteur RFID tape dans la page : les touches s'accumulent ici jusqu'à
  // Entrée. Les gestionnaires lisent l'état courant par cette référence.
  const tampon = useRef('');
  const occupe = useRef(false);
  const etat = useRef({});
  etat.current = { mode, lecture };

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => {
        setToast({ show: false, message: '', type: '' });
      }, 3500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [toast.show, toast.message]);

  // Les règles du jour, relues toutes les dix minutes (un réglage modifié, un
  // nouveau jour).
  useEffect(() => {
    let actif = true;
    const charger = async () => {
      try {
        const reponse = await api.pointages.kiosqueRegles();
        if (actif && reponse?.success) setReglesDuJour(reponse.data);
      } catch (error) {
        console.warn('[Kiosque] Règles du jour illisibles:', error.message);
      }
    };
    charger();
    const minuterie = setInterval(charger, RAFRAICHIR_REGLES_MS);
    return () => {
      actif = false;
      clearInterval(minuterie);
    };
  }, []);

  // Une lecture non confirmée s'abandonne : le kiosque ne reste pas bloqué
  // sur les règles de quelqu'un qui est parti.
  useEffect(() => {
    if (!lecture) return undefined;
    const minuterie = setTimeout(() => {
      if (etat.current.lecture !== lecture) return;
      setToast({ show: true, message: 'Pointage non enregistré : la lecture n\'a pas été confirmée', type: 'error' });
      setLecture(null);
      setMode('attente');
    }, DELAI_ABANDON_LECTURE_MS);
    return () => clearTimeout(minuterie);
  }, [lecture]);

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);

      try {
        await fetch('/api/auth/logout', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          credentials: 'include',
          signal: controller.signal
        });
        clearTimeout(timeoutId);
      } catch (fetchError) {
        clearTimeout(timeoutId);
      }
    } catch (error) {
      console.error('[Kiosque] Erreur déconnexion:', error);
    }

    localStorage.removeItem('user');
    localStorage.removeItem('isLoggedInNew');
    localStorage.removeItem('token');
    localStorage.removeItem('userPermissions');
    localStorage.removeItem('permissionsTimestamp');
    localStorage.removeItem('lastToken');

    setTimeout(() => {
      setShowGoodbye(true);
    }, 500);
  };

  const handleLogoutEmployee = () => {
    setEmployeeIdentified(null);
    setPointageStatus(null);
    setPointageMessage('');
    setRevenirA(null);
    setCreneauInfo(null);
    setGroupeInfo(null);
    setMode('attente');
  };

  const revenirALHorloge = (delai) => {
    setTimeout(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        handleLogoutEmployee();
        setIsTransitioning(false);
      }, 300);
    }, delai);
  };

  /** Le pointage lui-même, à l'heure du PREMIER passage du badge. */
  const handleAutoPointage = async (employeeData, badge, instant) => {
    setPointageStatus('loading');
    setPointageMessage('Enregistrement en cours...');

    try {
      const pointageData = {
        employe_id: employeeData.id,
        date_pointage: formatDateForAPI(instant),
        heure_pointage_reelle: instant.toISOString(),
        statut: 'present'
      };

      const response = await api.pointages.enregistrerBadgeuse(pointageData);

      if (response.success) {
        const isCreneauPointage = Boolean(response.creneau);
        const creneauType = response.type_pointage_creneau || response.creneau?.type_pointage || (
          response.type_pointage === 'depart' ? 'fin' : 'debut'
        );
        // Quatre types possibles côté personnel fixe depuis que la pause se
        // badge : une sortie de pause ne s'annonce pas comme un départ.
        const LIBELLES_POINTAGE = {
          arrivee: 'arrivée',
          pause_sortie: 'sortie de pause',
          pause_retour: 'retour de pause',
          depart: 'départ'
        };
        const typeMessage = isCreneauPointage
          ? (creneauType === 'fin' ? 'fin de cours' : 'début de cours')
          : (LIBELLES_POINTAGE[response.type_pointage] || 'départ');

        if (response.creneau) {
          setCreneauInfo({
            matiere: response.creneau.matiere,
            classe: response.creneau.classe,
            horaire: response.creneau.horaire,
            statut: response.statut,
            type: creneauType,
            ecart_minutes: response.creneau.ecart_minutes ?? response.ecart_minutes,
            message: response.creneau.message || response.message
          });
        }
        if (response.groupe) {
          setGroupeInfo({
            horaire_debut: response.groupe.horaire_debut,
            horaire_fin: response.groupe.horaire_fin,
            statut: response.statut,
            type: response.type_pointage
          });
        }

        setPointageStatus('success');
        // Les badges de pause reviennent avec une précision utile - l'heure de
        // retour attendue, la durée écoulée : on la montre.
        const estPause = response.type_pointage === 'pause_sortie' || response.type_pointage === 'pause_retour';
        setPointageMessage(
          estPause && response.message
            ? response.message
            : `Pointage ${typeMessage} enregistré avec succès`
        );

        const updatedEmployee = await api.employes.verifyBadge(badge);
        if (updatedEmployee.success && updatedEmployee.data) {
          setEmployeeIdentified(updatedEmployee.data);
        }
        revenirALHorloge(3000);
      } else {
        // Un refus dit quand revenir : on laisse le temps de le lire.
        const heureRetour = response.data?.revenir_a || null;
        setPointageStatus('error');
        setPointageMessage(response.message || 'Erreur lors du pointage');
        setRevenirA(heureRetour);
        revenirALHorloge(heureRetour ? 6000 : 4500);
      }
    } catch (error) {
      console.error('[Kiosque] Erreur pointage:', error);
      let errorMessage = 'Erreur lors du pointage';
      if (error.message && (error.message.includes('Network') || error.message.includes('network'))) {
        errorMessage = 'Erreur de connexion au serveur';
      } else if (error.data?.message) {
        errorMessage = error.data.message;
      }
      setPointageStatus('error');
      setPointageMessage(errorMessage);
      revenirALHorloge(4500);
    }
  };

  const lancerPointage = (employe, badge, instant) => {
    setIsTransitioning(true);
    showToast(`Bienvenue ${employe.first_name} ${employe.last_name}`, 'success');
    setTimeout(() => {
      setEmployeeIdentified(employe);
      setMode('pointage');
      setIsTransitioning(false);
    }, 300);
    setTimeout(() => handleAutoPointage(employe, badge, instant), 800);
  };

  /** Un badge inconnu du kiosque : qui est-ce, et doit-il d'abord lire ses règles ? */
  const identifier = async (badge, instant) => {
    occupe.current = true;
    setVerificationLoading(true);
    try {
      const response = await api.employes.verifyBadge(badge);
      if (!(response.success && response.data)) {
        showToast('Badge non reconnu dans le système', 'error');
        return;
      }
      const employe = response.data;

      // Les règles ne bloquent jamais un pointage : illisibles, on pointe.
      let regles = null;
      try {
        const reponseRegles = await api.pointages.kiosqueReglesPersonne(employe.id);
        if (reponseRegles?.success) regles = reponseRegles.data;
      } catch (error) {
        console.warn('[Kiosque] Règles de la personne illisibles:', error.message);
      }

      if (regles?.lecture?.requise) {
        setLecture({ employe, badge, instant, regles, debut: Date.now() });
        setMode('lecture');
        return;
      }
      lancerPointage(employe, badge, instant);
    } catch (error) {
      console.error('[Kiosque] Erreur vérification badge:', error);
      const reseau = error.message && (error.message.includes('Network') || error.message.includes('network'));
      showToast(reseau ? 'Erreur de connexion au serveur' : 'Badge non reconnu', 'error');
    } finally {
      occupe.current = false;
      setVerificationLoading(false);
    }
  };

  /** Le second passage du badge : la lecture est confirmée, le pointage part. */
  const confirmerLecture = async () => {
    const enCours = etat.current.lecture;
    if (!enCours) return;
    const secondes = (Date.now() - enCours.debut) / 1000;
    const reste = Math.ceil(Number(enCours.regles.dureeLectureSecondes || 0) - secondes);
    if (reste > 0) {
      showToast(`Prenez le temps de lire : encore ${reste} s`, 'error');
      return;
    }
    occupe.current = true;
    try {
      await api.pointages.kiosqueLecture({
        employe_id: enCours.employe.id,
        version: enCours.regles.version,
        duree_lecture_secondes: Math.round(secondes)
      });
    } catch (error) {
      // Une lecture non consignée sera redemandée la prochaine fois ; elle
      // n'empêche pas de pointer aujourd'hui.
      console.warn('[Kiosque] Lecture non consignée:', error.message);
    } finally {
      occupe.current = false;
    }
    setLecture(null);
    lancerPointage(enCours.employe, enCours.badge, enCours.instant);
  };

  const annulerLecture = () => {
    setLecture(null);
    setMode('attente');
    showToast('Pointage non enregistré', 'error');
  };

  const traiterBadge = (badge) => {
    if (!badge.trim() || occupe.current) return;
    const { mode: modeActuel, lecture: enCours } = etat.current;
    if (modeActuel === 'pointage') return;
    if (modeActuel === 'lecture' && enCours) {
      if (badge === enCours.badge) {
        confirmerLecture();
        return;
      }
      // Une autre personne badge : la lecture en cours est abandonnée plutôt
      // que de bloquer la file.
      showToast(`Lecture de ${enCours.employe.first_name} abandonnée : son pointage n'est pas enregistré`, 'error');
      setLecture(null);
      setMode('attente');
    }
    identifier(badge, new Date());
  };

  if (showGoodbye) {
    return <KiosqueGoodbyeScreen user={user} />;
  }

  const secondesRestantes = lecture
    ? Math.max(0, Math.ceil(Number(lecture.regles.dureeLectureSecondes || 0) - (currentTime.getTime() - lecture.debut) / 1000))
    : 0;
  const dureeLecture = Number(lecture?.regles?.dureeLectureSecondes || 1);
  const teinteLecture = TEINTES[lecture?.regles?.categorie === 'enseignant' ? 'enseignant' : 'personnel'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-100 to-gray-200">

      {/* Le lecteur RFID tape ici, quel que soit l'écran affiché. */}
      <input
        ref={(input) => {
          if (input && mode !== 'pointage') input.focus();
        }}
        type="text"
        value=""
        onChange={() => {}}
        onKeyDown={(e) => {
          if (e.keyCode === 16 || e.keyCode === 17 || e.keyCode === 18) {
            return;
          }
          if (e.keyCode === 13) {
            e.preventDefault();
            const badge = tampon.current;
            tampon.current = '';
            traiterBadge(badge);
            return;
          }
          let char = '';
          if (e.keyCode >= 48 && e.keyCode <= 57) {
            char = String.fromCharCode(e.keyCode);
          } else if (e.keyCode >= 96 && e.keyCode <= 105) {
            char = String.fromCharCode(e.keyCode - 48);
          } else if (e.keyCode >= 65 && e.keyCode <= 90) {
            char = String.fromCharCode(e.keyCode);
          }
          if (char) {
            tampon.current += char;
          }
          e.preventDefault();
        }}
        onBlur={(e) => {
          setTimeout(() => e.target.focus(), 100);
        }}
        aria-label="Lecteur de badge"
        style={{
          position: 'absolute',
          left: '-9999px',
          width: '1px',
          height: '1px',
          opacity: 0
        }}
      />

      <div className="flex h-screen">

        {/* PANNEAU GAUCHE */}
        <div className="w-2/3 p-8 flex flex-col">

          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-white border-4 border-[#3084b5] flex items-center justify-center shadow-lg">
                <User size={32} className="text-[#3084b5]" />
              </div>
              <div>
                <div className="text-lg font-bold text-gray-800">
                  {`${user.prenom || ''} ${user.nom || ''}`}
                </div>
                <div className="text-sm text-gray-600">
                  {user.role_principal?.nom || 'Non défini'}
                </div>
              </div>
            </div>

            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="px-6 py-3 bg-[#c66d59] hover:bg-[#b55d4a] text-white font-bold rounded-xl transition-all duration-300 disabled:opacity-50 flex items-center gap-2 shadow-lg hover:shadow-xl"
            >
              {isLoggingOut ? <Loader2 size={20} className="animate-spin" /> : <LogOut size={20} />}
              <span>{isLoggingOut ? 'Déconnexion...' : 'Déconnexion'}</span>
            </button>
          </div>

          <div className="flex-1 flex items-center justify-center min-h-0">
            <div className={`w-full ${mode === 'lecture' ? 'max-w-4xl' : 'max-w-2xl'} transition-all duration-500 ${
              isTransitioning ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
            }`}>
              {mode === 'lecture' && lecture ? (
                // ======================================== LECTURE OBLIGATOIRE
                <div className="animate-fade-in">
                  <div className="p-1.5 rounded-[40px] shadow-2xl" style={{ background: 'linear-gradient(135deg, #319190, #3084b5, #c66d59)' }}>
                    <div className="bg-white px-10 py-8 rounded-[36px] flex flex-col max-h-[calc(100vh-11rem)]">
                      <div className="flex items-center gap-5 pb-5 border-b-2 border-gray-100">
                        <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#3084b5] to-[#319190] flex items-center justify-center shadow-xl flex-shrink-0">
                          <User size={32} className="text-white" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-2xl font-bold text-[#3084b5]">
                            {lecture.employe.first_name} {lecture.employe.last_name}
                          </div>
                          <div className="text-sm text-gray-600">{lecture.regles.intitule}</div>
                        </div>
                      </div>

                      <div className="pt-5">
                        <div className="text-[18px] font-bold text-gray-900">
                          {lecture.regles.lecture?.motif === 'changement'
                            ? 'Les règles de pointage ont changé'
                            : 'Avant votre premier pointage, lisez vos règles'}
                        </div>
                        <p className="mt-1 text-sm text-gray-600">
                          Votre pointage sera enregistré à {lecture.instant.toLocaleTimeString('fr-FR')}, l&apos;heure de votre premier passage.
                        </p>
                      </div>

                      <div className="mt-4 flex-1 min-h-0 overflow-y-auto pr-1">
                        {lecture.regles.cours?.length > 0 && (
                          <div className="mb-5">
                            <div className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: teinteLecture.texte }}>Vos cours aujourd&apos;hui</div>
                            <table className="w-full border-collapse text-sm">
                              <thead>
                                <tr className="text-white" style={{ backgroundColor: teinteLecture.fonce }}>
                                  <th className="px-3 py-2 text-left font-bold">Cours</th>
                                  <th className="px-3 py-2 font-bold">Horaire</th>
                                  <th className="px-3 py-2 font-bold">Début à badger</th>
                                  <th className="px-3 py-2 font-bold">Fin à badger</th>
                                </tr>
                              </thead>
                              <tbody>
                                {lecture.regles.cours.map((c, index) => (
                                  <tr key={`${c.cours}-${c.horaire}`} className={index % 2 ? 'bg-gray-50' : 'bg-white'}>
                                    <td className="border border-gray-200 px-3 py-2 text-gray-800">{c.cours}</td>
                                    <td className="border border-gray-200 px-3 py-2 text-center text-gray-800 whitespace-nowrap">{c.horaire}</td>
                                    <td className="border border-gray-200 px-3 py-2 text-center text-gray-800 whitespace-nowrap">
                                      {c.debut}
                                      <div className="text-xs text-gray-500">retard jusqu&apos;à {c.retardJusqua}</div>
                                    </td>
                                    <td className="border border-gray-200 px-3 py-2 text-center text-gray-800 whitespace-nowrap">{c.fin}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        <Consignes consignes={lecture.regles.consignes} couleur={teinteLecture.texte} />
                      </div>

                      <div className="pt-5 mt-4 border-t-2 border-gray-100">
                        {secondesRestantes > 0 ? (
                          <div>
                            <div className="flex items-center justify-between text-sm font-bold text-gray-600 mb-2">
                              <span>Lisez vos règles</span>
                              <span>encore {secondesRestantes} s</span>
                            </div>
                            <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
                              <div
                                className="h-full rounded-full bg-[#3084b5] transition-all duration-1000 ease-linear"
                                style={{ width: `${Math.min(100, ((dureeLecture - secondesRestantes) / dureeLecture) * 100)}%` }}
                              />
                            </div>
                          </div>
                        ) : (
                          <div className="rounded-2xl bg-[#319190] text-white py-4 px-6 text-center">
                            <div className="flex items-center justify-center gap-3">
                              <Radio size={24} className="animate-pulse" />
                              <span className="text-lg font-bold">Repassez votre badge pour confirmer et pointer</span>
                            </div>
                          </div>
                        )}
                        <div className="mt-3 text-center">
                          <button type="button" onClick={annulerLecture} className="text-sm font-bold text-gray-500 hover:text-gray-700 underline">
                            Annuler
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : mode !== 'pointage' || !employeeIdentified ? (
                // ======================================== HORLOGE
                <div className="animate-fade-in">
                  <div className="p-1.5 rounded-[40px] shadow-2xl" style={{ background: 'linear-gradient(135deg, #319190, #3084b5, #c66d59)' }}>
                    <div className="bg-white p-12 rounded-[36px] relative">
                      <div className="text-center mb-6 animate-slide-down">
                        <div className="text-2xl font-bold text-[#3084b5] capitalize">
                          {currentTime.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                        </div>
                      </div>

                      <div className="text-center mb-8 animate-slide-down" style={{ animationDelay: '0.1s' }}>
                        <div className="text-8xl font-black text-[#3084b5]" style={{ fontFamily: 'monospace', letterSpacing: '0.05em' }}>
                          {currentTime.toLocaleTimeString('fr-FR')}
                        </div>
                      </div>

                      <div className="bg-[#3084b5] text-white rounded-2xl py-4 px-6 text-center animate-slide-down" style={{ animationDelay: '0.2s' }}>
                        <div className="flex items-center justify-center gap-3">
                          {verificationLoading ? <Loader2 size={24} className="animate-spin" /> : <Radio size={24} className="animate-pulse" />}
                          <span className="text-lg font-bold">{verificationLoading ? 'IDENTIFICATION...' : 'EN ATTENTE DE SCAN BADGE'}</span>
                        </div>
                      </div>

                      <p className="mt-6 text-center text-[15px] text-gray-600 animate-slide-down" style={{ animationDelay: '0.3s' }}>
                        Avant de badger, lisez les règles du jour à droite : un badge trop tôt ou trop tard est refusé.
                        {reglesDuJour?.commun?.map((c) => <span key={c.titre} className="block mt-1">{c.texte}</span>)}
                      </p>

                      <div className="flex justify-center mt-6 animate-slide-down" style={{ animationDelay: '0.3s' }}>
                        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#3084b5] to-[#319190] flex items-center justify-center shadow-xl">
                          <CreditCard size={40} className="text-white" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                // ======================================== RÉSULTAT DU BADGE
                <div className="animate-fade-in">
                  <div className="p-1.5 rounded-[40px] shadow-2xl" style={{ background: 'linear-gradient(135deg, #319190, #3084b5, #c66d59)' }}>
                    <div className="bg-white p-12 rounded-[36px] relative">

                      <div className="flex items-center gap-6 mb-8 pb-8 border-b-2 border-gray-100 animate-slide-down">
                        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#3084b5] to-[#319190] flex items-center justify-center shadow-xl">
                          <User size={40} className="text-white" />
                        </div>
                        <div>
                          <div className="text-3xl font-bold text-[#3084b5] mb-2">
                            {employeeIdentified.first_name} {employeeIdentified.last_name}
                          </div>
                          <div className="flex gap-2">
                            {employeeIdentified.position && (
                              <span className="px-3 py-1 bg-[#3084b5]/10 text-[#3084b5] rounded-lg text-sm font-bold">
                                {employeeIdentified.position}
                              </span>
                            )}
                            {employeeIdentified.department && (
                              <span className="px-3 py-1 bg-[#319190]/10 text-[#319190] rounded-lg text-sm font-bold">
                                {employeeIdentified.department}
                              </span>
                            )}
                            {employeeIdentified.type_contrat && (
                              <span className={`px-3 py-1 rounded-lg text-sm font-bold ${
                                employeeIdentified.type_contrat === 'HORAIRE'
                                  ? 'bg-purple-100 text-purple-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {employeeIdentified.type_contrat}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {employeeIdentified.type_contrat === 'HORAIRE' ? (
                        creneauInfo && (
                          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-6 mb-6 border-2 border-purple-200 animate-slide-down" style={{ animationDelay: '0.1s' }}>
                            <div className="flex items-center gap-2 mb-4">
                              <BookOpen size={20} className="text-purple-600" />
                              <div className="text-purple-700 font-bold text-sm">COURS DÉTECTÉ</div>
                            </div>
                            <div className="grid grid-cols-3 gap-4 mb-4">
                              <div className="bg-white rounded-xl p-3 border border-purple-200">
                                <div className="flex items-center gap-2 mb-1">
                                  <BookOpen size={14} className="text-purple-600" />
                                  <div className="text-xs text-purple-600">Matière</div>
                                </div>
                                <div className="text-lg font-bold text-purple-900">{creneauInfo.matiere}</div>
                              </div>
                              <div className="bg-white rounded-xl p-3 border border-purple-200">
                                <div className="flex items-center gap-2 mb-1">
                                  <UsersIcon size={14} className="text-purple-600" />
                                  <div className="text-xs text-purple-600">Classe</div>
                                </div>
                                <div className="text-lg font-bold text-purple-900">{creneauInfo.classe}</div>
                              </div>
                              <div className="bg-white rounded-xl p-3 border border-purple-200">
                                <div className="flex items-center gap-2 mb-1">
                                  <Clock size={14} className="text-purple-600" />
                                  <div className="text-xs text-purple-600">Horaire</div>
                                </div>
                                <div className="text-lg font-bold text-purple-900">{creneauInfo.horaire}</div>
                              </div>
                            </div>
                            <div className="flex items-center justify-center gap-3 pt-4 border-t-2 border-purple-200">
                              {creneauInfo.statut === 'a_temps' ? (
                                <>
                                  <CheckCircle size={24} className="text-green-600" />
                                  <span className="text-green-800 font-bold text-lg">À l&apos;heure</span>
                                </>
                              ) : creneauInfo.statut === 'retard' ? (
                                <>
                                  <AlertCircle size={24} className="text-orange-600" />
                                  <span className="text-orange-800 font-bold text-lg">Retard</span>
                                </>
                              ) : creneauInfo.statut === 'trop_tot' ? (
                                <>
                                  <TrendingUp size={24} className="text-blue-600" />
                                  <span className="text-blue-800 font-bold text-lg">Trop tôt</span>
                                </>
                              ) : (
                                <span className="text-gray-600">{creneauInfo.statut}</span>
                              )}
                              <span className={`ml-2 px-3 py-1 rounded-lg text-sm font-bold flex items-center gap-1 ${
                                creneauInfo.type === 'debut' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {creneauInfo.type === 'debut' ? (
                                  <>
                                    <ArrowRight size={14} />
                                    <span>Début</span>
                                  </>
                                ) : (
                                  <>
                                    <ArrowLeft size={14} />
                                    <span>Fin</span>
                                  </>
                                )}
                              </span>
                            </div>
                          </div>
                        )
                      ) : (
                        groupeInfo && (
                          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-6 mb-6 border-2 border-blue-200 animate-slide-down" style={{ animationDelay: '0.1s' }}>
                            <div className="flex items-center gap-2 mb-4">
                              <Clock size={20} className="text-blue-600" />
                              <div className="text-blue-700 font-bold text-sm">HORAIRE DE TRAVAIL</div>
                            </div>
                            <div className="grid grid-cols-2 gap-4 mb-4">
                              <div className="bg-white rounded-xl p-4 border border-blue-200">
                                <div className="flex items-center gap-2 mb-1">
                                  <TrendingUp size={16} className="text-blue-600" />
                                  <div className="text-xs text-blue-600">Début</div>
                                </div>
                                <div className="text-2xl font-bold text-blue-900">
                                  {groupeInfo.horaire_debut?.substring(0, 5) || '--:--'}
                                </div>
                              </div>
                              <div className="bg-white rounded-xl p-4 border border-blue-200">
                                <div className="flex items-center gap-2 mb-1">
                                  <TrendingDown size={16} className="text-blue-600" />
                                  <div className="text-xs text-blue-600">Fin</div>
                                </div>
                                <div className="text-2xl font-bold text-blue-900">
                                  {groupeInfo.horaire_fin?.substring(0, 5) || '--:--'}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center justify-center gap-3 pt-4 border-t-2 border-blue-200">
                              {groupeInfo.statut === 'a_temps' ? (
                                <>
                                  <CheckCircle size={24} className="text-green-600" />
                                  <span className="text-green-800 font-bold text-lg">À l&apos;heure</span>
                                </>
                              ) : groupeInfo.statut === 'retard' ? (
                                <>
                                  <AlertCircle size={24} className="text-orange-600" />
                                  <span className="text-orange-800 font-bold text-lg">Retard</span>
                                </>
                              ) : null}
                              <span className={`ml-2 px-3 py-1 rounded-lg text-sm font-bold flex items-center gap-1 ${
                                groupeInfo.type === 'arrivee' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {groupeInfo.type === 'arrivee' ? (
                                  <>
                                    <ArrowRight size={14} />
                                    <span>Arrivée</span>
                                  </>
                                ) : (
                                  <>
                                    <ArrowLeft size={14} />
                                    <span>Départ</span>
                                  </>
                                )}
                              </span>
                            </div>
                          </div>
                        )
                      )}

                      {/* Le message du pointage, et l'heure à laquelle revenir après un refus */}
                      <div className={`rounded-2xl py-4 px-6 text-center mb-8 border-2 animate-slide-down transition-all duration-500 ${
                        pointageStatus === 'loading'
                          ? 'bg-[#c66d59]/10 border-[#c66d59]/30'
                          : pointageStatus === 'success'
                            ? 'bg-[#319190]/10 border-[#319190]/30'
                            : pointageStatus === 'error'
                              ? 'bg-red-500/10 border-red-500/30'
                              : 'bg-gray-100 border-gray-300'
                      }`} style={{ animationDelay: (creneauInfo || groupeInfo) ? '0.2s' : '0.1s' }}>
                        <div className="flex items-center justify-center gap-3">
                          {pointageStatus === 'loading' && <Loader2 size={24} className="animate-spin text-[#c66d59]" />}
                          {pointageStatus === 'success' && <CheckCircle size={24} className="text-[#319190] animate-scale-in" />}
                          {pointageStatus === 'error' && <X size={24} className="text-red-500 animate-scale-in flex-shrink-0" />}
                          <span className={`text-lg font-bold transition-colors duration-300 ${
                            pointageStatus === 'loading'
                              ? 'text-[#c66d59]'
                              : pointageStatus === 'success'
                                ? 'text-[#319190]'
                                : pointageStatus === 'error'
                                  ? 'text-red-500'
                                  : 'text-gray-500'
                          }`}>
                            {pointageMessage || 'EN ATTENTE...'}
                          </span>
                        </div>
                        {pointageStatus === 'error' && revenirA && (
                          <div className="mt-3 pt-3 border-t border-red-500/20">
                            <div className="text-sm font-bold text-gray-600">Revenez badger à partir de</div>
                            <div className="text-4xl font-black text-[#3084b5] font-mono">{revenirA}</div>
                          </div>
                        )}
                      </div>

                      {employeeIdentified.type_contrat !== 'HORAIRE' && (
                        <div className="grid grid-cols-2 gap-4 animate-slide-down" style={{ animationDelay: groupeInfo ? '0.3s' : '0.2s' }}>
                          <div className={`rounded-2xl p-6 transition-all duration-500 ${
                            employeeIdentified.a_pointe_arrivee
                              ? 'bg-gradient-to-br from-[#319190] to-[#2a7a7a] text-white shadow-xl scale-105'
                              : 'bg-gray-100 text-gray-400'
                          }`}>
                            <div className="flex items-center gap-2 mb-3">
                              <CheckCircle size={24} className={employeeIdentified.a_pointe_arrivee ? 'text-white' : 'text-gray-400'} />
                              <span className="font-bold text-lg">Arrivée</span>
                            </div>
                            {employeeIdentified.a_pointe_arrivee ? (
                              <div className="text-4xl font-black font-mono">
                                {employeeIdentified.pointage_arrivee?.heure || '--:--'}
                              </div>
                            ) : (
                              <div className="text-lg font-bold">En attente</div>
                            )}
                          </div>
                          <div className={`rounded-2xl p-6 transition-all duration-500 ${
                            employeeIdentified.a_pointe_depart
                              ? 'bg-gradient-to-br from-[#c66d59] to-[#b55d4a] text-white shadow-xl scale-105'
                              : 'bg-gray-100 text-gray-400'
                          }`}>
                            <div className="flex items-center gap-2 mb-3">
                              <X size={24} className={employeeIdentified.a_pointe_depart ? 'text-white' : 'text-gray-400'} />
                              <span className="font-bold text-lg">Départ</span>
                            </div>
                            {employeeIdentified.a_pointe_depart ? (
                              <div className="text-4xl font-black font-mono">
                                {employeeIdentified.pointage_depart?.heure || '--:--'}
                              </div>
                            ) : (
                              <div className="text-lg font-bold">En attente</div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* PANNEAU DROIT : l'école et les règles de pointage du jour */}
        <div className="w-1/3 bg-[#3084b5] p-5 flex flex-col gap-3 overflow-y-auto">
          <div className="bg-white/10 backdrop-blur rounded-2xl px-5 py-4 animate-fade-in">
            <SchoolHeader />
          </div>

          <div className="px-1 text-white text-[18px] font-bold">Règles de pointage du jour</div>

          {reglesDuJour ? (
            <>
              {reglesDuJour.personnel && (
                <CarteRegles titre="Personnel à horaire fixe" teinte={TEINTES.personnel} etiquette={reglesDuJour.personnel.horaire}>
                  <LignesRegles consignes={reglesDuJour.personnel.consignes} teinte={TEINTES.personnel} />
                  {reglesDuJour.personnel.groupes?.length > 0 && (
                    <div className={`space-y-2 ${reglesDuJour.personnel.consignes.length ? 'mt-3 pt-3 border-t border-gray-100' : ''}`}>
                      {reglesDuJour.personnel.groupes.map((g) => (
                        <div key={g.nom}>
                          <div className="text-[11px] font-bold uppercase tracking-wide" style={{ color: TEINTES.personnel.texte }}>Groupe {g.nom} ({g.horaire})</div>
                          <div className="text-[14px] leading-snug text-gray-800">{g.texte}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </CarteRegles>
              )}

              {reglesDuJour.enseignants && (
                <CarteRegles titre="Enseignants payés à l'heure" teinte={TEINTES.enseignant} etiquette="Par cours">
                  <LignesRegles consignes={reglesDuJour.enseignants.consignes} teinte={TEINTES.enseignant} />
                  {reglesDuJour.enseignants.exemple && (
                    <div className="mt-3 rounded-xl px-4 py-2.5" style={{ backgroundColor: TEINTES.enseignant.clair }}>
                      <div className="text-[11px] font-bold uppercase tracking-wide" style={{ color: TEINTES.enseignant.texte }}>Exemple</div>
                      <div className="text-[14px] leading-snug text-gray-800">{reglesDuJour.enseignants.exemple}</div>
                    </div>
                  )}
                </CarteRegles>
              )}

            </>
          ) : (
            <div className="bg-white/10 backdrop-blur rounded-2xl p-5 text-[15px] text-white">
              Approchez votre badge du lecteur : le kiosque vous dira si le pointage est accepté.
            </div>
          )}
        </div>
      </div>

      {/* Toast */}
      {toast.show && (
        <div className={`fixed top-8 right-8 px-6 py-4 rounded-xl shadow-2xl z-50 animate-slide-in ${
          toast.type === 'success' ? 'bg-[#319190]' : 'bg-[#c66d59]'
        }`}>
          <div className="flex items-center gap-3">
            {toast.type === 'success' ? (
              <CheckCircle size={24} className="text-white" />
            ) : (
              <X size={24} className="text-white" />
            )}
            <p className="text-white font-bold text-lg">{toast.message}</p>
          </div>
        </div>
      )}

      {/* Styles CSS pour les animations */}
      <style>{`
        @keyframes slide-in {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes fade-in {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes slide-down {
          from { opacity: 0; transform: translateY(-20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes scale-in {
          from { transform: scale(0); }
          to { transform: scale(1); }
        }
        .animate-slide-in { animation: slide-in 0.3s ease-out; }
        .animate-fade-in { animation: fade-in 0.5s ease-out; }
        .animate-slide-down { animation: slide-down 0.5s ease-out; animation-fill-mode: both; }
        .animate-scale-in { animation: scale-in 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default KiosquePointage;
